import React, { useRef } from 'react';

let instances = 0;

/**
 * LIA's mascot: a navy speech bubble ("lia" is word or speech in Tetum) with a friendly face,
 * a gold kaibauk crescent on top and a tais band woven with diamond and triangle motifs.
 * Pure SVG, so it stays sharp from the 24px chat avatar to the page hero.
 *
 * `title` makes it a labelled image; without it the mascot is decorative (hidden from screen readers).
 */
export default function LiaMascot({ size = 96, title, className, style }) {
    // React 17 has no useId; SVG ids must be unique when several mascots share a page.
    const idRef = useRef(null);
    if (idRef.current === null) {
        instances += 1;
        idRef.current = `lia-${instances}`;
    }
    const id = idRef.current;

    return (
        <svg
            width={size}
            height={size}
            viewBox="0 0 120 120"
            className={className}
            style={style}
            role={title ? 'img' : undefined}
            aria-label={title}
            aria-hidden={title ? undefined : true}
            focusable="false"
        >
            <defs>
                <linearGradient id={`${id}-body`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0" stopColor="#225491" />
                    <stop offset="0.55" stopColor="#143a66" />
                    <stop offset="1" stopColor="#0e2748" />
                </linearGradient>
                <linearGradient id={`${id}-gold`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0" stopColor="#f4d97a" />
                    <stop offset="1" stopColor="#c8a02a" />
                </linearGradient>
                {/* Tais weave: red ground, gold diamond, black and white triangles. */}
                <pattern id={`${id}-tais`} width="16" height="16" patternUnits="userSpaceOnUse">
                    <rect width="16" height="16" fill="#a62121" />
                    <path d="M8 2 L14 8 L8 14 L2 8 Z" fill="#c8a02a" />
                    <path d="M8 5 L11 8 L8 11 L5 8 Z" fill="#0e2748" />
                    <path d="M0 0 L4 0 L0 4 Z M16 0 L12 0 L16 4 Z M0 16 L4 16 L0 12 Z M16 16 L12 16 L16 12 Z" fill="#111827" />
                    <circle cx="8" cy="8" r="1" fill="#ffffff" />
                </pattern>
                <clipPath id={`${id}-clip`}>
                    <rect x="14" y="28" width="92" height="76" rx="30" />
                </clipPath>
            </defs>

            {/* Kaibauk: the gold crescent headdress, horns pointing up */}
            <path
                d="M22 10 C33 34, 87 34, 98 10 C85 25, 35 25, 22 10 Z"
                fill={`url(#${id}-gold)`}
                stroke="#a47b00"
                strokeWidth="1"
            />
            <circle cx="60" cy="27" r="3.4" fill={`url(#${id}-gold)`} stroke="#a47b00" strokeWidth="1" />

            {/* Speech-bubble body with its tail */}
            <path d="M30 98 L22 116 L46 102 Z" fill="#0e2748" />
            <rect x="14" y="28" width="92" height="76" rx="30" fill={`url(#${id}-body)`} />

            {/* Tais band across the bottom, following the bubble's rounded corners */}
            <g clipPath={`url(#${id}-clip)`}>
                <rect x="10" y="84" width="100" height="22" fill={`url(#${id}-tais)`} />
                <rect x="10" y="82" width="100" height="3" fill="#c8a02a" />
            </g>

            {/* Face */}
            <ellipse cx="46" cy="56" rx="9" ry="10" fill="#ffffff" />
            <ellipse cx="74" cy="56" rx="9" ry="10" fill="#ffffff" />
            <circle cx="47.5" cy="58" r="4.6" fill="#0e2748" />
            <circle cx="75.5" cy="58" r="4.6" fill="#0e2748" />
            <circle cx="49.3" cy="55.8" r="1.6" fill="#ffffff" />
            <circle cx="77.3" cy="55.8" r="1.6" fill="#ffffff" />
            <circle cx="35" cy="69" r="4" fill="#ff8f80" opacity="0.6" />
            <circle cx="85" cy="69" r="4" fill="#ff8f80" opacity="0.6" />
            <path d="M50 71 Q60 79 70 71" fill="none" stroke="#f4d97a" strokeWidth="3.6" strokeLinecap="round" />
        </svg>
    );
}
